import { useQuery } from '@tanstack/react-query';
import { formatDate as fmtDate, formatCurrency as fmtCurrency, formatSpeed as fmtSpeed, type DateFormat, type Currency, type DecimalSeparator, type SpeedUnit } from '../utils/locale-format';
import { useAuthStore } from '../store/auth.store';
import { usePermission } from './usePermission';
import { fetchScopedConfig } from '../api/config-scope.api';

const DEFAULTS = {
  dateFormat: 'dd/MM/yyyy' as const,
  currency: 'MXN' as const,
  decimalSeparator: '.' as const,
  speedUnit: 'km/h' as const,
};

export function useLocaleSettings() {
  const tenantId = useAuthStore(s => s.user?.tenantId);
  const canReadConfig = usePermission('config:read');

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-config', tenantId],
    queryFn: () => fetchScopedConfig('TENANT', tenantId as string),
    enabled: !!tenantId && canReadConfig,
  });

  const effective = data?.effective ?? {};
  const dateFormat = (effective['locale.dateFormat']?.value ?? DEFAULTS.dateFormat) as DateFormat;
  const currency = (effective['locale.currency']?.value ?? DEFAULTS.currency) as Currency;
  const decimalSeparator = (effective['locale.decimalSeparator']?.value ?? DEFAULTS.decimalSeparator) as DecimalSeparator;
  const speedUnit = (effective['locale.speedUnit']?.value ?? DEFAULTS.speedUnit) as SpeedUnit;

  return {
    formatDate: (value: Date | string | undefined | null) => fmtDate(value, dateFormat),
    /** `override` formatea en la moneda del registro (p. ej. USD) en vez de la moneda base del tenant. */
    formatCurrency: (value: number, override?: string | null) => {
      const code = override === 'USD' || override === 'MXN' ? override : currency;
      const text = fmtCurrency(value, code, decimalSeparator);
      // Dólares en un tenant con otra moneda base: "US$" para no confundirlos con "$" a secas.
      return code === 'USD' && code !== currency ? text.replace('$', 'US$') : text;
    },
    formatSpeed: (kmh: number) => fmtSpeed(kmh, speedUnit),
    dateFormat,
    currency,
    decimalSeparator,
    isLoading,
  };
}
